import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { FetchCSVAction, DeleteCSVAction } from "../../../Actions/Analysis";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import {
  FetchIndividualFileAction,
  AddAnalysisFileIDToRedux,
} from "../../../Actions/Analysis";
import { ChevronLeft, ChevronRight, EllipsisVertical, Eye, FileX, Trash2 } from "lucide-react";
import TableLoader from "../../Common/TableLoader";
import Tooltip from "../../Common/Tooltip";
import { getCached, setCached, clearCached } from "../../../Utils/fileCache";

const PAGE_SIZE = 10;
const STATUS_CACHE_KEY = (fileType, page) => `upload-file-status-${fileType || "all"}-p${page}`;

class UploadFileStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      openActionMenuId: null,
      loading: true,
      viewingId: null,
      currentPage: 1,
      totalCount: 0,
    };
  }

  static propTypes = {
    FetchCSVAction: PropTypes.func.isRequired,
    DeleteCSVAction: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    FetchIndividualFileAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    csvAnalysisSuccess: PropTypes.bool,
    pdfAnalysisSuccess: PropTypes.bool,
  };

  componentDidMount() {
    this.fetchFiles();
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.pdfAnalysisSuccess &&
        this.props.pdfAnalysisSuccess !== prevProps.pdfAnalysisSuccess) ||
      (this.props.csvAnalysisSuccess &&
        this.props.csvAnalysisSuccess !== prevProps.csvAnalysisSuccess)
    ) {
      this.fetchFiles();
    }
  }

  handleClickOutside = () => {
    if (this.state.openActionMenuId !== null) {
      this.setState({ openActionMenuId: null });
    }
  };

  getFileType = () => {
    const { pathname } = this.props.location;
    if (pathname === "/dashboard") return "csv";
    if (pathname === "/dashboard/pdf-analyzer") return "pdf";
    return null;
  };

  fetchFiles = async (page = 1) => {
    const fileType = this.getFileType();
    const cacheKey = STATUS_CACHE_KEY(fileType, page);
    const cached = getCached(cacheKey);
    if (cached) {
      this.setState({ files: cached.results, totalCount: cached.count, currentPage: page, loading: false });
      // Background refresh
      this.props.FetchCSVAction(page, fileType, PAGE_SIZE).then((response) => {
        if (response) {
          setCached(cacheKey, response);
          this.setState({ files: response.results || [], totalCount: response.count || 0 });
        }
      }).catch(() => {});
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await this.props.FetchCSVAction(page, fileType, PAGE_SIZE);
      if (response) {
        setCached(cacheKey, response);
        this.setState({ files: response.results || [], totalCount: response.count || 0, currentPage: page, loading: false });
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      this.setState({ loading: false });
    }
  };

  truncateFileName = (fileName, maxLength = 20) => {
    if (!fileName || fileName.length <= maxLength) return fileName || "No file name";
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = extensionIndex !== -1 ? fileName.slice(extensionIndex) : "";
    const nameWithoutExt = extensionIndex !== -1 ? fileName.slice(0, extensionIndex) : fileName;
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);
    return `${truncatedName}...${extension}`;
  };

  toggleActionMenu = (fileId) => {
    this.setState((prevState) => ({
      openActionMenuId: prevState.openActionMenuId === fileId ? null : fileId,
    }));
  };

  handleViewFile = (file) => {
    if (this.state.viewingId) return;
    this.setState({ openActionMenuId: null, viewingId: file.id });
    this.props.FetchIndividualFileAction(file.id).then((res) => {
      const fileType = res?.file_type?.toUpperCase();
      if (fileType === "CSV") {
        this.props.navigate(`result-summary/${file.id}`);
      } else if (fileType === "PDF") {
        this.props.navigate(`/dashboard/pdf-analyzer/result-summary/${file.id}`);
      }
      this.setState({ viewingId: null });
    }).catch(() => {
      this.setState({ viewingId: null });
    });
  };

  handlePageChange = (newPage) => {
    this.fetchFiles(newPage);
  };

  handleDelete = async (file) => {
    this.setState({ openActionMenuId: null });
    const { currentPage, totalCount } = this.state;
    const newTotal = totalCount - 1;
    const targetPage = Math.min(currentPage, Math.ceil(newTotal / PAGE_SIZE) || 1);

    // Optimistically remove from list immediately so UI feels instant
    this.setState((prev) => ({
      files: prev.files.filter((f) => f.id !== file.id),
      totalCount: newTotal,
    }));
    clearCached("upload-file-status");

    try {
      await this.props.DeleteCSVAction(file.id);
    } catch (err) {
      // swallow — re-fetch below will restore correct state if delete failed
    }
    this.fetchFiles(targetPage);
  };

  render() {
    const { openActionMenuId, files, loading, viewingId, currentPage, totalCount } = this.state;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

    return (
      <div className="flex flex-col h-full pb-6">
        <div className="flex-1 bg-white border border-blue-100 rounded-xl flex flex-col">
          {/* Title bar */}
          <div className="px-6 py-4 border-b border-blue-100 rounded-t-xl" style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}>
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              Files
            </p>
          </div>
          {/* Column headers */}
          <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr] items-center py-2 bg-gray-50/50 px-5 gap-4 border-b border-gray-100">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">File Name</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Date Uploaded</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">File Size</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Status</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Actions</div>
          </div>

          {/* File Rows */}
          <div className="flex-1">
          {loading ? (
            <TableLoader />
          ) : files.length > 0 ? (
            files.map((file) => (
              <div
                key={file.id}
                className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr] py-3.5 px-5 gap-4 border-t border-gray-100 font-inter text-sm text-gray-700 hover:bg-blue-50/40 transition-colors"
              >
                <div className="font-medium text-gray-900 overflow-visible">
                  <Tooltip text={file.file_name || "No file name"} icon={false}>
                    <span className="cursor-default">{this.truncateFileName(file.file_name)}</span>
                  </Tooltip>
                </div>
                <div className="text-gray-500 truncate">
                  {new Date(file.uploaded_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <div className="text-gray-500">{file.file_size || "—"}</div>
                <div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      file.processing_status?.toLowerCase() === "processed"
                        ? "bg-green-50 text-green-700 border-green-100"
                        : file.processing_status?.toLowerCase() === "not processed"
                        ? "bg-red-50 text-red-600 border-red-100"
                        : "bg-yellow-50 text-yellow-700 border-yellow-100"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      file.processing_status?.toLowerCase() === "processed"
                        ? "bg-green-500"
                        : file.processing_status?.toLowerCase() === "not processed"
                        ? "bg-red-500"
                        : "bg-yellow-500"
                    }`} />
                    {file.processing_status}
                  </span>
                </div>
                <div className="relative flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => this.toggleActionMenu(file.id)}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                    aria-label="More actions"
                  >
                    <EllipsisVertical className="w-4 h-4" />
                  </button>
                  {openActionMenuId === file.id && (
                    <div className="absolute right-0 top-8 z-20 w-36 bg-white border border-gray-100 rounded-lg shadow-custom-shadow py-1 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => this.handleViewFile(file)}
                        disabled={!!viewingId}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-primary-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {viewingId === file.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin shrink-0" />
                            Opening…
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4" />
                            View
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => this.handleDelete(file)}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : null}
          </div>
          {files.length === 0 && !loading && (
            <div className="flex flex-1 w-full flex-col items-center justify-center gap-6 py-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)" }}>
                <FileX className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-600">
                  No files uploaded yet
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Upload a CSV or data file to get started
                </p>
              </div>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">
                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} files
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => this.handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-500 px-2 font-medium">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => this.handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  pdfAnalysisSuccess: state.analysis.pdfAnalysisSuccess,
  csvAnalysisSuccess: state.analysis.csvAnalysisSuccess,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchCSVAction,
    DeleteCSVAction,
    FetchIndividualFileAction,
    AddAnalysisFileIDToRedux,
  })(UploadFileStatus)
);
